import { Card, CardBody, CardHeader, CardTitle } from '@vnetwork/ui/primitives';
import { BarList, Stat, StatGroup } from '@vnetwork/ui/data';
import type { OpsOrgDetail } from '@/api/operations-org-hooks';
import { orgCategoryTotal } from '@/api/operations-org-hooks';
import { InfoGrid } from '@/pages/operations/detail/tabs/ops-detail-shared';
import { CATEGORY_LABEL } from '@/pages/operations/detail/tabs/ops-detail-shared';
import type { OpsCategory } from '@/api/operations';

const DISPLAY_CATS: OpsCategory[] = ['cdn', 'waap', 'compute', 'storage', 'media', 'registrar'];

interface Props {
  data: OpsOrgDetail;
}

/**
 * Org detail Overview tab — customer info fact grid + KPI stat band +
 * per-category service breakdown.
 */
export function OpsOrgOverviewTab({ data }: Props) {
  const customer = data.customer;

  const customerRows = customer
    ? [
        { label: 'Customer ID', value: customer.customer_id },
        { label: 'Name', value: customer.customer_name },
        ...(customer.customer_company_name
          ? [{ label: 'Company', value: customer.customer_company_name }]
          : []),
      ]
    : [{ label: 'DataCentral', value: 'Not found — internal or unregistered org' }];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* KPI strip */}
      <Card flat>
        <CardBody>
          <StatGroup columns={4}>
            <Stat
              label="Total services"
              value={data.services.total.toLocaleString('en-US')}
              dotColor="var(--vn-primary-solid)"
            />
            <Stat
              label="Teams"
              value={data.teams_count.toLocaleString('en-US')}
              dotColor="var(--vn-info-solid)"
            />
            <Stat
              label="Projects"
              value={data.projects_count.toLocaleString('en-US')}
              dotColor="var(--vn-success-solid)"
            />
            <Stat
              label="Domains"
              value={data.domain_count.toLocaleString('en-US')}
              dotColor="var(--vn-fg-muted)"
            />
          </StatGroup>
        </CardBody>
      </Card>

      {/* Two-column layout: customer info + category breakdown */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--vn-space-4)',
        }}
      >
        {/* Customer info */}
        <Card flat>
          <CardHeader>
            <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
              Customer
            </CardTitle>
          </CardHeader>
          <CardBody>
            <InfoGrid rows={customerRows.map((r) => ({ label: r.label, value: String(r.value) }))} />
          </CardBody>
        </Card>

        {/* Per-category service counts */}
        <Card flat>
          <CardHeader>
            <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
              Services by category
            </CardTitle>
          </CardHeader>
          <CardBody>
            {data.services.total === 0 ? (
              <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                No services
              </span>
            ) : (
              <BarList
                items={DISPLAY_CATS.flatMap((cat) => {
                  const count = orgCategoryTotal(data.services, cat);
                  if (!count) return [];
                  const active = data.services.by_category?.[cat]?.['active'] ?? 0;
                  const terminated = data.services.by_category?.[cat]?.['terminated'] ?? 0;
                  return [{
                    label: CATEGORY_LABEL[cat],
                    value: count,
                    display: `${active} active${terminated > 0 ? `, ${terminated} terminated` : ''}`,
                  }];
                })}
              />
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
